import {
  atimInterfaceToLocaleString,
  hitchTypeToLocaleString,
} from "common/utils/enumTranslations";
import { capitalize, Paper, Typography } from "@mui/material";
import { FieldPreview } from "../../../tractors/tractor-editor/steps/ReviewStep";
import { ImplementViewModel } from "../ImplementViewModel";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC } from "react";

export const ReviewStep: FC = () => {
  const { values } = useFormikContext<ImplementViewModel>();
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        <InfoOutlinedIcon className="flex my-auto" />
        <Typography>
          {t("views.autotractor.equipment.atkSetupReminderImplement")}
        </Typography>
      </div>
      <Paper className="flex flex-col gap-2 p-4">
        <FieldPreview
          label={capitalize(t("utils.descriptors.name"))}
          name="name"
          value={values.name}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.make_one"))}
          name="archetype.make"
          archetypeValue={values.archetype?.make}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.model_one"))}
          name="archetype.model"
          archetypeValue={values.archetype?.model}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.series"))}
          name="archetype.series"
          archetypeValue={values.archetype?.series}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.hitchType"))}
          name="hitchType"
          archetypeValue={
            values.archetype?.hitchType
              ? capitalize(
                  t(hitchTypeToLocaleString(values.archetype.hitchType))
                )
              : capitalize(t("utils.descriptors.none"))
          }
        />
        <FieldPreview
          label={capitalize(t("models.equipment.atimInterface"))}
          name="archetype.atim"
          archetypeValue={
            values.archetype?.atim
              ? capitalize(
                  t(atimInterfaceToLocaleString(values.archetype.atim))
                )
              : capitalize(t("utils.descriptors.none"))
          }
        />
        <Typography className="font-bold">
          {t("views.autotractor.equipment.autonomyMeasurements")}
        </Typography>
        <div className="flex flex-col gap-2 ml-4">
          <FieldPreview
            label={capitalize(t("models.equipment.implement.workingWidth"))}
            name="workingWidthM"
            value={values.workingWidthM}
            archetypeValue={values.archetype?.workingWidthM}
            dimensionInfo={{
              storedUnit: "m",
              displayUnits: {
                imperial: "ft",
                metric: "m",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.implement.toolSpeed"))}
            name="toolSpeedMS"
            value={values.toolSpeedMS}
            archetypeValue={values.archetype?.toolSpeedMS}
            dimensionInfo={{
              storedUnit: "m/s",
              displayUnits: {
                imperial: "mph",
                metric: "km/h",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.implement.transitionSpeed"))}
            name="transitionSpeedMS"
            value={values.transitionSpeedMS}
            archetypeValue={values.archetype?.transitionSpeedMS}
            dimensionInfo={{
              storedUnit: "m/s",
              displayUnits: {
                imperial: "mph",
                metric: "km/h",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.implement.endOffset"))}
            name="workingEndOffsetM"
            value={values.workingEndOffsetM}
            archetypeValue={values.archetype?.workingEndOffsetM}
            dimensionInfo={{
              storedUnit: "m",
              displayUnits: {
                imperial: "in",
                metric: "cm",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.implement.startOffset"))}
            name="workingStartOffsetM"
            value={values.workingStartOffsetM}
            archetypeValue={values.archetype?.workingStartOffsetM}
            dimensionInfo={{
              storedUnit: "m",
              displayUnits: {
                imperial: "in",
                metric: "cm",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.implement.runUpDistance"))}
            name="runUpDistanceM"
            value={values.runUpDistanceM}
            archetypeValue={values.archetype?.runUpDistanceM}
            dimensionInfo={{
              storedUnit: "m",
              displayUnits: {
                imperial: "ft",
                metric: "m",
              },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(
              t("models.equipment.implement.straightenDistance")
            )}
            name="straightenDistanceM"
            value={values.straightenDistanceM}
            archetypeValue={values.archetype?.straightenDistanceM}
            dimensionInfo={{
              storedUnit: "m",
              displayUnits: {
                imperial: "ft",
                metric: "m",
              },
              localSystem: measurementSystem,
            }}
          />
        </div>
        <Typography className="font-bold">
          {capitalize(t("models.equipment.pto_short"))}
        </Typography>
        <div className="flex flex-col gap-2 ml-4">
          <FieldPreview
            label={capitalize(t("utils.descriptors.type_one"))}
            name="ptoRequirements.type"
            value={values.ptoRequirements?.type}
            archetypeValue={values.archetype?.ptoRequirements?.type}
          />
          <FieldPreview
            label={capitalize(t("utils.descriptors.speed_one"))}
            name="ptoRequirements.ptoSpeed"
            value={values.ptoRequirements?.ptoSpeed}
            archetypeValue={values.archetype?.ptoRequirements?.ptoSpeed}
          />
          <FieldPreview
            label={capitalize(t("models.equipment.engineSpeed"))}
            name="ptoRequirements.engineSpeedRpm"
            value={values.ptoRequirements?.engineSpeedRpm}
            archetypeValue={values.archetype?.ptoRequirements?.engineSpeedRpm}
            dimensionInfo={{
              storedUnit: "rpm",
              displayUnits: {
                imperial: "rpm",
                metric: "rpm",
              },
              localSystem: measurementSystem,
            }}
          />
        </div>
        <Typography className="font-bold">
          {capitalize(t("models.equipment.scv_short", { count: 1 }))}
        </Typography>
        <div className="pl-4 flex flex-col gap-4">
          {values.scvInputs &&
            values.scvInputs.inputs.map((s, i) => (
              <div key={`scv-${i}`} className="flex flex-col gap-2">
                <FieldPreview
                  label={capitalize(t("models.equipment.function"))}
                  name={`archetype.scvInputs.inputs[${i}].function`}
                  value={s.function}
                />
                <div className="flex flex-col gap-2 ml-4">
                  <FieldPreview
                    label={t(
                      "views.autotractor.equipment.implements.recommendedTractorPort"
                    )}
                    name={`archetype.scvInputs.inputs[${i}].recommendedPort`}
                    value={s.recommendedPort}
                  />
                  <FieldPreview
                    label={capitalize(t("models.equipment.couplerDiameter"))}
                    name={`archetype.scvInputs.inputs[${i}].couplerDiameterMm`}
                    value={s.couplerDiameterMm}
                    dimensionInfo={{
                      storedUnit: "mm",
                      displayUnits: {
                        imperial: "in",
                        metric: "mm",
                      },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
          {!values.scvInputs &&
            values.archetype?.scvInputs?.inputs.map((s, i) => (
              <div key={`scv-${i}`} className="flex flex-col gap-2">
                <FieldPreview
                  label={capitalize(t("models.equipment.function"))}
                  name={`scvInputs.inputs[${i}].function`}
                  value={s.function}
                />
                <div className="flex flex-col gap-2 ml-4">
                  <FieldPreview
                    label={t(
                      "views.autotractor.equipment.implements.recommendedTractorPort"
                    )}
                    name={`scvInputs.inputs[${i}].recommendedPort`}
                    value={s.recommendedPort}
                  />
                  <FieldPreview
                    label={capitalize(t("models.equipment.couplerDiameter"))}
                    name={`scvInputs.inputs[${i}].couplerDiameterMm`}
                    value={s.couplerDiameterMm}
                    dimensionInfo={{
                      storedUnit: "mm",
                      displayUnits: {
                        imperial: "in",
                        metric: "mm",
                      },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </Paper>
    </div>
  );
};
